import {
    ADMIN_GRADUATION_STYLES,
    ADMIN_GRADUATION_THEMES,
    ADMIN_GRAPHIC_STYLES,
    ADMIN_LIBRARY_ASSETS,
    ADMIN_PATTERN_CATEGORIES,
    ADMIN_RESOURCE_CATEGORIES,
} from '@/mocks/adminLibrary';
import { localized } from '@/mocks/adminLocales';
import { defineStore } from 'pinia';
import { ref, toRaw } from 'vue';
import type { IAdminCategory, IAdminGraduationStyle, IAdminLibraryAsset } from '@/mocks/adminLibrary';

type TAdminLibraryClassification = 'graduationThemes' | 'graphicStyles' | 'patternCategories' | 'resourceCategories';

// Back-office mock-up: the simulated library in memory. To be replaced by /admin/library and
// /admin/library/:id/files (phase 1 back).
const useAdminLibraryStore = defineStore('adminLibrary', () => {
    const graduations = ref<IAdminGraduationStyle[]>(structuredClone(ADMIN_GRADUATION_STYLES));
    const assets = ref<IAdminLibraryAsset[]>(structuredClone(ADMIN_LIBRARY_ASSETS));
    const classifications = ref<Record<TAdminLibraryClassification, IAdminCategory[]>>({
        graduationThemes: structuredClone(ADMIN_GRADUATION_THEMES),
        graphicStyles: structuredClone(ADMIN_GRAPHIC_STYLES),
        patternCategories: structuredClone(ADMIN_PATTERN_CATEGORIES),
        resourceCategories: structuredClone(ADMIN_RESOURCE_CATEGORIES),
    });

    function saveClassification(kind: TAdminLibraryClassification, categories: IAdminCategory[]): void {
        classifications.value[kind] = structuredClone(toRaw(categories));
    }

    // A new graduation design starts as a draft with no file, in the first theme and graphic
    // style; it joins the library when its drawer is saved.
    function newGraduation(): IAdminGraduationStyle {
        return {
            id: `graduation-${Date.now()}`,
            name: localized('Nouveau modèle'),
            themeId: classifications.value.graduationThemes[0]?.id ?? '',
            graphicStyleId: classifications.value.graphicStyles[0]?.id ?? '',
            isActive: false,
            isRecolorable: true,
            compatiblePrintStyles: null,
            files: [],
        };
    }

    function deleteGraduation(id: string): void {
        graduations.value = graduations.value.filter((graduation) => graduation.id !== id);
    }

    // A detached copy for the drawer to edit. structuredClone cannot read Vue's proxies, hence toRaw.
    function getGraduationCopy(id: string): IAdminGraduationStyle | undefined {
        const graduation = graduations.value.find((candidate) => candidate.id === id);
        return graduation && structuredClone(toRaw(graduation));
    }

    function saveGraduation(updated: IAdminGraduationStyle): void {
        const index = graduations.value.findIndex((graduation) => graduation.id === updated.id);
        if (index === -1) {
            graduations.value.unshift(structuredClone(toRaw(updated)));
            return;
        }
        graduations.value[index] = structuredClone(toRaw(updated));
    }

    function setGraduationActive(id: string, isActive: boolean): void {
        const graduation = graduations.value.find((candidate) => candidate.id === id);
        if (!graduation) {
            console.error(`Admin library: no graduation ${id} to toggle`);
            return;
        }
        graduation.isActive = isActive;
    }

    // A detached copy for the drawer to edit. structuredClone cannot read Vue's proxies, hence toRaw.
    function getAssetCopy(id: string): IAdminLibraryAsset | undefined {
        const asset = assets.value.find((candidate) => candidate.id === id);
        return asset && structuredClone(toRaw(asset));
    }

    function saveAsset(updated: IAdminLibraryAsset): void {
        const index = assets.value.findIndex((asset) => asset.id === updated.id);
        if (index === -1) {
            assets.value.unshift(structuredClone(toRaw(updated)));
            return;
        }
        assets.value[index] = structuredClone(toRaw(updated));
    }

    function deleteAsset(id: string): void {
        assets.value = assets.value.filter((asset) => asset.id !== id);
    }

    return {
        assets,
        classifications,
        deleteAsset,
        deleteGraduation,
        getAssetCopy,
        getGraduationCopy,
        graduations,
        newGraduation,
        saveAsset,
        saveClassification,
        saveGraduation,
        setGraduationActive,
    };
});

export { useAdminLibraryStore };
